import { localDate, type CalendarMonthResponse } from '@veriverse/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../net/api.js';
import { celebrantsToday, joinNames } from '../state/birthday-calendar.js';
import { useSessionStore } from '../state/session-store.js';
import { OFFICE_TIME_ZONE } from '../state/welcome-weather.js';

const AUTO_HIDE_MS = 30_000;

/** `userId:yyyy-m-d` of the last banner shown in this page load, so it shows once per login. */
let shownFor: string | undefined;

/**
 * "🎉 Happy birthday, X!" across the top of the office on someone's birthday (spec §6.13).
 * Shown once per login: re-entering the office after a queue or reconnect does not repeat it.
 */
export function BirthdayBanner() {
  const { t, i18n } = useTranslation();
  const userId = useSessionStore((s) => s.user?.id);
  const [names, setNames] = useState<string[]>([]);

  useEffect(() => {
    // Read the rest once, on entry: later profile edits must not re-trigger the banner.
    const user = useSessionStore.getState().user;
    if (!userId || !user) return;
    const today = localDate(new Date(), OFFICE_TIME_ZONE);
    const key = `${user.id}:${today.year}-${today.month}-${today.day}`;
    if (shownFor === key) return;
    let current = true;
    const me = { id: user.id, displayName: user.displayName, birthday: user.birthday };
    api<CalendarMonthResponse>(`/api/calendar?year=${today.year}&month=${today.month}`)
      .then((body) => body.birthdays)
      .catch(() => []) // the user's own birthday still shows if the calendar fails
      .then((birthdays) => {
        if (!current) return;
        const found = celebrantsToday(me, birthdays, today);
        if (found.length === 0) return;
        shownFor = key;
        setNames(found);
      });
    return () => {
      current = false;
    };
  }, [userId]);

  useEffect(() => {
    if (names.length === 0) return;
    const timer = window.setTimeout(() => setNames([]), AUTO_HIDE_MS);
    return () => window.clearTimeout(timer);
  }, [names]);

  if (names.length === 0) return null;
  return (
    <div className="birthday-banner" role="status">
      <span aria-hidden="true">🎉</span>
      {t('birthday.banner', { names: joinNames(names, i18n.language) })}
      <button
        type="button"
        className="secondary"
        onClick={() => setNames([])}
        aria-label={t('common.dismiss')}
      >
        ×
      </button>
    </div>
  );
}
